import { defaultAppUserId, supabase } from '../config/supabase';

type TeamFeeCampaign = {
  id: string;
  amount_per_player: number;
  assignment_granularity: 'per_team' | 'per_player';
};

type TeamContext = {
  organization_id: string | null;
};

type RosterPlayer = {
  player_id: string;
};

type ExistingAssignment = {
  fee_campaign_id: string;
  player_id: string;
  team_id: string | null;
};

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') return error.message;
  return 'Unknown error';
}

async function syncTeamFeeAssignmentsFromClient(teamId: string, playerIds?: string[], feeCampaignId?: string, actorId = defaultAppUserId): Promise<number> {
  const { data: teamData, error: teamError } = await supabase
    .from('teams')
    .select('organization_id')
    .eq('id', teamId)
    .is('deleted_at', null)
    .single();
  if (teamError) throw new Error(teamError.message);

  let rosterQuery = supabase
    .from('team_rosters')
    .select('player_id')
    .eq('team_id', teamId)
    .eq('is_active', true)
    .neq('roster_status', 'inactive')
    .is('deleted_at', null);
  if (playerIds && playerIds.length > 0) rosterQuery = rosterQuery.in('player_id', playerIds);

  const { data: rosterData, error: rosterError } = await rosterQuery;
  if (rosterError) throw new Error(rosterError.message);

  const rosterPlayerIds = Array.from(new Set(((rosterData || []) as unknown as RosterPlayer[]).map((row) => row.player_id)));
  if (rosterPlayerIds.length === 0) return 0;

  let scopedCampaignIds = feeCampaignId ? [feeCampaignId] : [];
  if (!feeCampaignId) {
    const { data: scopeData, error: scopeError } = await supabase
      .from('player_fee_assignments')
      .select('fee_campaign_id')
      .eq('team_id', teamId)
      .is('deleted_at', null);
    if (scopeError) throw new Error(scopeError.message);
    scopedCampaignIds = Array.from(new Set(((scopeData || []) as unknown as { fee_campaign_id: string }[]).map((row) => row.fee_campaign_id)));
  }
  if (scopedCampaignIds.length === 0) return 0;

  const { data: campaignData, error: campaignError } = await supabase
    .from('team_fee_campaigns')
    .select('id, amount_per_player, assignment_granularity')
    .in('id', scopedCampaignIds)
    .eq('active_status', true)
    .is('deleted_at', null);
  if (campaignError) throw new Error(campaignError.message);

  const campaigns = (campaignData || []) as unknown as TeamFeeCampaign[];
  if (campaigns.length === 0) return 0;

  const { data: existingData, error: existingError } = await supabase
    .from('player_fee_assignments')
    .select('fee_campaign_id, team_id, player_id')
    .in('fee_campaign_id', campaigns.map((campaign) => campaign.id))
    .in('player_id', rosterPlayerIds)
    .is('deleted_at', null);
  if (existingError) throw new Error(existingError.message);

  const existingRows = (existingData || []) as unknown as ExistingAssignment[];
  const existingTeamKeys = new Set(existingRows.map((row) => `${row.fee_campaign_id}:${row.team_id || ''}:${row.player_id}`));
  const existingPlayerKeys = new Set(existingRows.map((row) => `${row.fee_campaign_id}:${row.player_id}`));
  const team = teamData as unknown as TeamContext;
  const rows = campaigns.flatMap((campaign) =>
    rosterPlayerIds
      .filter((playerId) => {
        if ((campaign.assignment_granularity || 'per_team') === 'per_player') {
          return !existingPlayerKeys.has(`${campaign.id}:${playerId}`);
        }
        return !existingTeamKeys.has(`${campaign.id}:${teamId}:${playerId}`);
      })
      .map((playerId) => ({
        fee_campaign_id: campaign.id,
        organization_id: team.organization_id || null,
        team_id: teamId,
        player_id: playerId,
        amount_owed: campaign.amount_per_player,
        payment_status: 'Unpaid',
        created_by: actorId,
        updated_by: actorId,
      })),
  );

  if (rows.length === 0) return 0;
  const { error: insertError } = await supabase.from('player_fee_assignments').insert(rows);
  if (insertError) throw new Error(insertError.message);
  return rows.length;
}

export async function syncTeamFeeAssignments(teamId: string, playerIds?: string[], feeCampaignId?: string, actorId = defaultAppUserId): Promise<number> {
  const { data, error } = await supabase.rpc('sync_team_fee_assignments', {
    p_team_id: teamId,
    p_player_ids: playerIds && playerIds.length > 0 ? playerIds : null,
    p_actor_id: actorId,
    p_fee_campaign_id: feeCampaignId || null,
  });
  if (!error) return Number(data || 0);

  try {
    return await syncTeamFeeAssignmentsFromClient(teamId, playerIds, feeCampaignId, actorId);
  } catch (fallbackError) {
    throw new Error(errorMessage(fallbackError) || errorMessage(error));
  }
}

export function feeAssignmentSyncErrorMessage(error: unknown): string {
  return errorMessage(error);
}
